/**
 * Centralized z-index configuration for the BluePill AI application
 *
 * This file defines all z-index values used throughout the application
 * to ensure proper layering and avoid conflicts.
 *
 * Z-Index Hierarchy (from lowest to highest):
 * - Base layers (0-9): Basic layout elements
 * - Content layers (10-19): Dropdowns, tooltips
 * - Sticky elements (20-29): Fixed headers, floating buttons
 * - Overlays (30-39): Backdrop overlays, side panels
 * - Modals (40-49): Modal dialogs and their backdrops
 * - Navigation (50-59): Navigation menus, mobile nav
 * - Popovers & Dialogs (60-69): Popovers, dialog boxes
 * - Critical UI (70-79): Context menus, select dropdowns
 * - Notifications (90-99): Toast messages, notifications
 * - Maximum (100): Highest priority elements
 */

export const zIndex = {
  // Base layers (0-9)
  base: 0,
  progressBar: 1,
  cardHover: 2,

  // Content layers (10-19)
  dropdown: 10,
  tooltip: 15,

  // Sticky elements (20-29)
  stickyHeader: 20,
  floatingButton: 25,

  // Overlays (30-39)
  overlay: 30,
  sidePanel: 35,

  // Modals (40-49)
  modalBackdrop: 40,
  modal: 45,

  // Navigation (50-59)
  mobileNav: 50,
  navDropdown: 55,
  progressSteps: 55, // Important: Same level as nav dropdown for clickability

  // Popovers & Dialogs (60-69)
  popover: 60,
  dialog: 65,

  // Critical UI (70-79)
  contextMenu: 70,
  select: 75,

  // Simulation panels (80-89)
  simulationInputsPanel: 80,

  // Notifications (90-99)
  notification: 90,
  toast: 95,

  // Maximum (100)
  maximum: 100,
} as const;

// Type for z-index values
export type ZIndexValue = (typeof zIndex)[keyof typeof zIndex];

// Helper function to get z-index as CSS value
export const getZIndex = (layer: keyof typeof zIndex): string => {
  return `${zIndex[layer]}`;
};

// Tailwind class mapping
export const zIndexClasses = {
  base: 'z-0',
  progressBar: 'z-[1]',
  cardHover: 'z-[2]',
  progressSteps: 'z-[5]',
  dropdown: 'z-10',
  tooltip: 'z-[15]',
  stickyHeader: 'z-20',
  floatingButton: 'z-[25]',
  overlay: 'z-30',
  sidePanel: 'z-[35]',
  modalBackdrop: 'z-40',
  modal: 'z-[45]',
  mobileNav: 'z-50',
  navDropdown: 'z-[55]',
  popover: 'z-[60]',
  dialog: 'z-[65]',
  contextMenu: 'z-[70]',
  select: 'z-[75]',
  simulationInputsPanel: 'z-[80]',
  notification: 'z-[90]',
  toast: 'z-[95]',
  maximum: 'z-[100]',
} as const;

// Export a function to get the appropriate Tailwind class
export const getZIndexClass = (layer: keyof typeof zIndex): string => {
  return zIndexClasses[layer];
};
